import { useState, useEffect, useRef } from "react";
import PageMeta from "@/components/common/PageMeta";
import PageBreadcrumb from "@/components/common/PageBreadCrumb";
import Label from "@/components/form/Label";
import Input from "@/components/form/input/InputField";
import { flockService } from "@/services/masterDataService";
import {
  flockReportService,
  type FlockProductionReport,
  type FlockReportRow,
} from "@/services/flockReportService";
import { handleApiError } from "@/services/api";
import type { Flock } from "@/types/models";

const selectClass =
  "h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-500 focus:outline-none focus:ring focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90";

type ColumnDef = {
  key: string;
  label: string;
  shortLabel: string;
  align: "left" | "right";
  render: (row: FlockReportRow) => string;
  colorClass?: string;
  defaultVisible: boolean;
};

const fmt = (v: number) => (v === 0 ? "-" : v.toLocaleString());
const fmtDec = (v: number) => (v === 0 ? "-" : v.toFixed(2));
const fmtDate = (d: string) => {
  const dt = new Date(d);
  return `${dt.getDate()}/${dt.getMonth() + 1}`;
};

const columns: ColumnDef[] = [
  { key: "date", label: "Date", shortLabel: "Date", align: "left", render: (r) => fmtDate(r.date), defaultVisible: true },
  { key: "age", label: "Age", shortLabel: "Age", align: "left", render: (r) => `${r.age_days}d / ${r.age_weeks}w`, defaultVisible: true },
  { key: "opening_birds", label: "Opening Birds", shortLabel: "Open", align: "right", render: (r) => fmt(r.opening_birds), defaultVisible: true },
  { key: "mortality", label: "Mortality", shortLabel: "Mort", align: "right", render: (r) => fmt(r.mortality), colorClass: "text-error-600 dark:text-error-400", defaultVisible: true },
  { key: "birds_sold", label: "Birds Sold", shortLabel: "Sold", align: "right", render: (r) => fmt(r.birds_sold), defaultVisible: true },
  { key: "closing_birds", label: "Closing Birds", shortLabel: "Close", align: "right", render: (r) => fmt(r.closing_birds), defaultVisible: true },
  { key: "cum_mortality", label: "Cum. Mortality", shortLabel: "Cum Mort", align: "right", render: (r) => fmt(r.cum_mortality), defaultVisible: false },
  { key: "cum_mortality_pct", label: "Mortality %", shortLabel: "Mort%", align: "right", render: (r) => fmtDec(r.cum_mortality_pct), defaultVisible: true },
  { key: "total_eggs", label: "Total Eggs", shortLabel: "Eggs", align: "right", render: (r) => fmt(r.total_eggs), colorClass: "text-success-700 dark:text-success-400 font-medium", defaultVisible: true },
  { key: "broken_eggs", label: "Broken Eggs", shortLabel: "Broken", align: "right", render: (r) => fmt(r.broken_eggs), defaultVisible: false },
  { key: "production_trays", label: "Trays", shortLabel: "Trays", align: "right", render: (r) => fmtDec(r.production_trays), defaultVisible: true },
  { key: "hdp_pct", label: "HDP %", shortLabel: "HDP%", align: "right", render: (r) => fmtDec(r.hdp_pct), colorClass: "font-semibold", defaultVisible: true },
  { key: "feed_kg", label: "Feed (kg)", shortLabel: "Feed kg", align: "right", render: (r) => fmtDec(r.feed_kg), defaultVisible: true },
  { key: "feed_per_bird_g", label: "Feed/Bird (g)", shortLabel: "g/Bird", align: "right", render: (r) => fmtDec(r.feed_per_bird_g), defaultVisible: true },
  { key: "feed_per_egg_g", label: "Feed/Egg (g)", shortLabel: "g/Egg", align: "right", render: (r) => fmtDec(r.feed_per_egg_g), defaultVisible: false },
  { key: "cum_feed_kg", label: "Cum. Feed (kg)", shortLabel: "Cum Feed", align: "right", render: (r) => fmtDec(r.cum_feed_kg), defaultVisible: false },
  { key: "cum_eggs", label: "Cum. Eggs", shortLabel: "Cum Eggs", align: "right", render: (r) => fmt(r.cum_eggs), defaultVisible: false },
  { key: "cum_feed_per_egg_g", label: "Cum Feed/Egg (g)", shortLabel: "Cum g/Egg", align: "right", render: (r) => fmtDec(r.cum_feed_per_egg_g), defaultVisible: false },
];

const STORAGE_KEY = "production-report-columns";

function loadVisibleColumns(): Set<string> {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) return new Set(JSON.parse(saved));
  } catch { /* ignore */ }
  return new Set(columns.filter((c) => c.defaultVisible).map((c) => c.key));
}

export default function ProductionReportPage() {
  const [flocks, setFlocks] = useState<Flock[]>([]);
  const [selectedFlockId, setSelectedFlockId] = useState<number | null>(null);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [report, setReport] = useState<FlockProductionReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [flocksLoading, setFlocksLoading] = useState(true);
  const [visibleCols, setVisibleCols] = useState<Set<string>>(loadVisibleColumns);
  const [colPickerOpen, setColPickerOpen] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    flockService
      .list()
      .then((data) => {
        const active = data.filter((f) => f.status === "active");
        setFlocks(active);
        if (active.length > 0) setSelectedFlockId(active[0].id);
      })
      .catch(handleApiError)
      .finally(() => setFlocksLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedFlockId) return;
    setLoading(true);
    flockReportService
      .getProductionReport(selectedFlockId, from || undefined, to || undefined)
      .then(setReport)
      .catch(handleApiError)
      .finally(() => setLoading(false));
  }, [selectedFlockId, from, to]);

  // Close picker on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setColPickerOpen(false);
      }
    };
    if (colPickerOpen) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [colPickerOpen]);

  const toggleColumn = (key: string) => {
    setVisibleCols((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      localStorage.setItem(STORAGE_KEY, JSON.stringify([...next]));
      return next;
    });
  };

  const activeCols = columns.filter((c) => visibleCols.has(c.key));

  return (
    <>
      <PageMeta title="Production Report | Poultry Book" description="Daily flock production report" />
      <PageBreadcrumb pageTitle="Production Report" />

      <div className="card">
        {/* Filters */}
        <div className="border-b border-gray-200 p-4 dark:border-gray-700">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
            <div>
              <Label>Flock</Label>
              <select
                className={selectClass}
                value={selectedFlockId ?? ""}
                onChange={(e) => setSelectedFlockId(Number(e.target.value))}
                disabled={flocksLoading}
              >
                {flocksLoading && <option>Loading...</option>}
                {flocks.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.batch_name} ({f.shed_code})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label>From</Label>
              <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </div>
            <div>
              <Label>To</Label>
              <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            </div>
            <div className="relative" ref={pickerRef}>
              <Label>Columns</Label>
              <button
                type="button"
                onClick={() => setColPickerOpen((v) => !v)}
                className={selectClass + " flex items-center justify-between cursor-pointer"}
              >
                <span>{activeCols.length} of {columns.length} shown</span>
                <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {colPickerOpen && (
                <div className="absolute right-0 z-50 mt-1 w-64 max-h-80 overflow-y-auto rounded-lg border border-gray-200 bg-white p-2 shadow-lg dark:border-gray-700 dark:bg-gray-900">
                  <div className="mb-2 flex gap-2 border-b border-gray-100 pb-2 dark:border-gray-700">
                    <button
                      type="button"
                      className="text-xs text-brand-500 hover:underline"
                      onClick={() => {
                        const all = new Set(columns.map((c) => c.key));
                        setVisibleCols(all);
                        localStorage.setItem(STORAGE_KEY, JSON.stringify([...all]));
                      }}
                    >
                      Show All
                    </button>
                    <button
                      type="button"
                      className="text-xs text-brand-500 hover:underline"
                      onClick={() => {
                        const defaults = new Set(columns.filter((c) => c.defaultVisible).map((c) => c.key));
                        setVisibleCols(defaults);
                        localStorage.setItem(STORAGE_KEY, JSON.stringify([...defaults]));
                      }}
                    >
                      Reset
                    </button>
                  </div>
                  {columns.map((col) => (
                    <label
                      key={col.key}
                      className="flex items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-gray-50 cursor-pointer dark:hover:bg-gray-800"
                    >
                      <input
                        type="checkbox"
                        checked={visibleCols.has(col.key)}
                        onChange={() => toggleColumn(col.key)}
                        className="rounded border-gray-300 text-brand-500 focus:ring-brand-500"
                      />
                      {col.label}
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>

          {report && (
            <div className="mt-3 flex flex-wrap gap-4 text-sm text-gray-600 dark:text-gray-400">
              <span><strong>Batch:</strong> {report.batch_name}</span>
              <span><strong>Arrival:</strong> {new Date(report.arrival_date).toLocaleDateString()}</span>
              <span><strong>Initial Birds:</strong> {report.initial_count.toLocaleString()}</span>
              <span><strong>Rows:</strong> {report.rows.length} days</span>
            </div>
          )}
        </div>

        {/* Scrollable Table */}
        <div className="overflow-x-auto">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading report...</div>
          ) : !report || report.rows.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              {selectedFlockId ? "No data for the selected period." : "Select a flock to view the report."}
            </div>
          ) : (
            <table className="w-max min-w-full">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50 dark:border-gray-700 dark:bg-gray-800">
                  {activeCols.map((col) => (
                    <th
                      key={col.key}
                      className={`whitespace-nowrap px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 ${
                        col.align === "right" ? "text-right" : "text-left"
                      }`}
                    >
                      {col.shortLabel}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {report.rows.map((row, i) => (
                  <tr
                    key={row.date}
                    className={`border-b border-gray-50 dark:border-gray-800 ${
                      i % 2 === 0 ? "" : "bg-gray-50/50 dark:bg-gray-800/30"
                    }`}
                  >
                    {activeCols.map((col) => (
                      <td
                        key={col.key}
                        className={`whitespace-nowrap px-4 py-2 text-sm ${
                          col.align === "right" ? "text-right" : "text-left"
                        } ${col.colorClass ?? "text-gray-700 dark:text-gray-300"}`}
                      >
                        {col.render(row)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
}
